import React, { useState, useEffect } from "react";

const HomeBanner = ({
  slides = [],
  fadeDuration = 1000, // Fade duration in ms
}) => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [windowWidth, setWindowWidth] = useState(
    typeof window !== "undefined" ? window.innerWidth : 1024
  );

  // Automatically cycle slides every 5 seconds
  useEffect(() => {
    if (!slides.length) return;
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [slides.length]);

  // Listen for window resize to switch between desktop and mobile images
  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // If no slides, return nothing
  if (!slides.length) return null;

  const currentSlideData = slides[currentSlide];
  const isMobile = windowWidth < 640;

  // Decide which image to use based on viewport
  const currentImgSrc =
    isMobile && currentSlideData.mobileImgSrc
      ? currentSlideData.mobileImgSrc
      : currentSlideData.imgSrc;

  return (
    <div className="relative isolate overflow-hidden w-full">
      {/** MOBILE VIEW (stacked image + content) */}
      {isMobile ? (
        <div className="fade-animation">
          <div className="relative">
            <img
              alt={currentSlideData.altText || ""}
              src={currentImgSrc}
              className="w-full h-auto object-cover pt-[25px]"
            />
            {currentSlideData.heading && (
              <div className="bg-neutral-900/50 absolute bottom-0 left-0 right-0 px-5 py-4">
                <h1 className="text-base text-white font-medium">
                  {currentSlideData.heading}
                </h1>
              </div>
            )}
          </div>
          <div className="px-5 py-4 bg-white">
            {currentSlideData.paragraphText && (
              <p className="text-sm font-normal">{currentSlideData.paragraphText}</p>
            )}
          </div>
        </div>
      ) : (
        /** DESKTOP VIEW (background image + overlaid content) */
        <div className="relative h-dvh pt-14 fade-animation">
          <img
            alt={currentSlideData.altText || ""}
            src={currentImgSrc}
            className="absolute inset-0 -z-10 object-cover w-full h-full"
          />
          {/** Semi-transparent overlay (optional) */}
          <div className="opacity-layer absolute inset-0 w-full h-full bg-neutral-900/20 z-0"></div>

          {/** Overlaid text */}
          <div className="absolute bottom-20 2xl:left-8">
            <div className="text-left px-6 lg:px-8 max-w-7xl mx-auto">
              {currentSlideData.heading && (
                <h1
                  className={`text-xl inline-flex font-semibold text-white lg:text-5xl 2xl:drop-shadow-lg ${currentSlideData.heading_width}`}
                >
                  {currentSlideData.heading}
                </h1>
              )}
              {currentSlideData.paragraphText && (
                <p
                  className={`mt-2 text-lg leading-7 text-white ${currentSlideData.paragraphText_width}`}
                >
                  {currentSlideData.paragraphText}
                </p>
              )}
            </div>
          </div>
        </div>
      )}
      <style jsx>{`
        .fade-animation {
          animation: fadeIn ${fadeDuration}ms ease-in-out;
        }
        @keyframes fadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }
      `}</style>
    </div>
  );
};

export default HomeBanner;
